import type {
  ContactEmailChangeBlockReason,
  ContactEmailChangePlan,
} from "../../../../../api/types/fundStructureTypes";

export type ContactEmailEditMode =
  | { kind: "inline" }
  | { kind: "disabled"; reason: string }
  | { kind: "dialog"; plan: ContactEmailChangePlan; hint: string };

const noPortalLoginText = "This contact doesn’t have a portal login, so there’s no sign-in email to change.";

const getBlockedText = (blockReason?: ContactEmailChangeBlockReason): string =>
  blockReason === "RegistrationByEmailInProgress"
    ? "This contact is part-way through registering by email. Changing the address now would cancel it."
    : "We couldn’t check what this contact’s login opens, so we can’t change it safely right now. Please try again later.";

const getChangeHint = (plan: ContactEmailChangePlan): string =>
  plan.samePersonOutcomeAtFreeAddress === "Split"
    ? "Gives this contact a separate login here. The contact registers with the new address, and the portal history moves across."
    : "Changes the address the contact signs in with. The password and everything in the portal stay the same.";

export const resolveContactEmailEditMode = (input: {
  portalLogin?: string;
  hasPortalApp: boolean;
  plan?: ContactEmailChangePlan;
  isPortalAccessLoading: boolean;
}): ContactEmailEditMode => {
  const { portalLogin, hasPortalApp, plan, isPortalAccessLoading } = input;

  if (!portalLogin || !hasPortalApp) {
    return { kind: "inline" };
  }

  if (isPortalAccessLoading) {
    return { kind: "disabled", reason: "Checking whether this contact’s email can be changed…" };
  }

  if (!plan) {
    return {
      kind: "disabled",
      reason: "We couldn’t check whether this contact’s email can be changed. Please try again later.",
    };
  }

  if (!plan.canChange) {
    return { kind: "disabled", reason: getBlockedText(plan.blockReason) };
  }

  return { kind: "dialog", plan, hint: getChangeHint(plan) };
};

export const getSignInEmailChangeTooltip = (mode: ContactEmailEditMode): string => {
  switch (mode.kind) {
    case "dialog":
      return mode.hint;
    case "disabled":
      return mode.reason;
    case "inline":
      return noPortalLoginText;
  }
};
